/**
 * Outing Helper Utilities
 * Modular functions for golf outing management.
 */

// Game format display names
export const GAME_FORMATS = {
  scramble: {
    name: 'Scramble',
    description: 'All players tee off, best shot is selected, all play from that spot.',
    icon: '🏌️'
  },
  best_ball: {
    name: 'Best Ball',
    description: 'Each player plays their own ball, team records the lowest score.',
    icon: '⭐'
  },
  shamble: {
    name: 'Shamble',
    description: 'Best drive selected, then each player plays their own ball.',
    icon: '🔀'
  },
  chapman: {
    name: 'Chapman (Pinehurst)',
    description: 'Partners hit, swap balls for second shot, then select best ball.',
    icon: '🤝'
  },
  alternate_shot: {
    name: 'Alternate Shot',
    description: 'Partners share one ball, alternating shots.',
    icon: '🔄'
  }
};

// Status display
export const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'gray', icon: '📝' },
  open: { label: 'Open', color: 'green', icon: '✅' },
  locked: { label: 'Locked', color: 'yellow', icon: '🔒' },
  in_progress: { label: 'In Progress', color: 'blue', icon: '⏳' },
  review: { label: 'Under Review', color: 'purple', icon: '👀' },
  completed: { label: 'Completed', color: 'emerald', icon: '🏆' },
  cancelled: { label: 'Cancelled', color: 'red', icon: '❌' }
};

// Payment status
export const PAYMENT_STATUS = {
  pending: { label: 'Pending', color: 'red', bgColor: 'bg-red-500/20' },
  partial: { label: 'Partial', color: 'yellow', bgColor: 'bg-yellow-500/20' },
  paid: { label: 'Paid', color: 'green', bgColor: 'bg-green-500/20' },
  waived: { label: 'Waived', color: 'blue', bgColor: 'bg-blue-500/20' },
  refunded: { label: 'Refunded', color: 'gray', bgColor: 'bg-gray-500/20' }
};

// Payment methods
export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: '💵' },
  { value: 'zelle', label: 'Zelle', icon: '📱' },
  { value: 'venmo', label: 'Venmo', icon: '💳' },
  { value: 'card', label: 'Card', icon: '💳' },
  { value: 'golf_coins', label: 'Golf Coins', icon: '🪙' }
];

// Format currency
export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(amount);
}

// Format date
export function formatDate(dateStr, options = {}) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: options.weekday || 'short',
    month: options.month || 'short',
    day: options.day || 'numeric',
    year: options.year || 'numeric'
  });
}

// Format time
export function formatTime(timeStr) {
  if (!timeStr) return '';
  // Convert 24h to 12h format
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Convert inches to feet and inches display
export function inchesToFeetDisplay(totalInches) {
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}ft ${inches}in`;
}

// Parse feet/inches string to total inches
export function feetDisplayToInches(display) {
  const match = display.match(/(\d+)\s*ft\s*(\d+)\s*in/i);
  if (match) {
    return parseInt(match[1], 10) * 12 + parseInt(match[2], 10);
  }
  return 0;
}

// Calculate score relative to par
export function calculateRelativeScore(score, par) {
  if (!score || !par) return null;
  const diff = score - par;
  if (diff === 0) return 'E';
  return diff > 0 ? `+${diff}` : `${diff}`;
}

// Get score style class based on relative score
export function getScoreStyleClass(score, par) {
  if (!score || !par) return '';
  const diff = score - par;
  
  if (diff <= -2) return 'text-yellow-400 font-bold'; // Eagle or better
  if (diff === -1) return 'text-green-400 font-semibold'; // Birdie
  if (diff === 0) return 'text-white'; // Par
  if (diff === 1) return 'text-red-400'; // Bogey
  if (diff >= 2) return 'text-red-500 font-semibold'; // Double bogey or worse
  
  return '';
}

// Validate team can be formed
export function validateTeam(members, teamSize) {
  if (members.length === 0) {
    return { valid: false, error: 'Team must have at least one member' };
  }
  if (members.length > teamSize) {
    return { valid: false, error: `Team cannot have more than ${teamSize} members` };
  }
  const hasCaptain = members.some(m => m.role === 'captain');
  if (!hasCaptain) {
    return { valid: false, error: 'Team must have a captain' };
  }
  return { valid: true };
}

// Group invitations by status
export function groupInvitationsByStatus(invitations) {
  return {
    pending: invitations.filter(i => i.status === 'pending'),
    accepted: invitations.filter(i => i.status === 'accepted'),
    declined: invitations.filter(i => i.status === 'declined'),
    expired: invitations.filter(i => i.status === 'expired')
  };
}

// Calculate payment summary
export function calculatePaymentSummary(teams, payments) {
  const totalDue = teams.reduce((sum, t) => sum + (t.total_due || 0), 0);
  const totalPaid = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const outstanding = Math.max(0, totalDue - totalPaid);
  const paidPercentage = totalDue > 0 ? (totalPaid / totalDue) * 100 : 0;
  
  return {
    totalDue,
    totalPaid,
    outstanding,
    paidPercentage: Math.min(100, paidPercentage)
  };
}

// Generate team name suggestions
export function generateTeamName(index) {
  const adjectives = ['Mighty', 'Golden', 'Flying', 'Royal', 'Iron', 'Silver', 'Thunder', 'Swift'];
  const nouns = ['Eagles', 'Tigers', 'Hawks', 'Lions', 'Bears', 'Wolves', 'Phoenix', 'Dragons'];
  
  if (index < adjectives.length * nouns.length) {
    const adj = adjectives[index % adjectives.length];
    const noun = nouns[Math.floor(index / adjectives.length) % nouns.length];
    return `${adj} ${noun}`;
  }
  
  return `Team ${index + 1}`;
}

// Check if user is organizer
export function isOrganizer(outing, userId) {
  return outing?.organizer_id === userId;
}

// Check if user is team captain
export function isTeamCaptain(team, userId) {
  return team?.captain_id === userId;
}

// Get notification style based on type
export function getNotificationStyle(type) {
  switch (type) {
    case 'hole_in_one':
      return {
        bgClass: 'bg-gradient-to-r from-yellow-500/30 to-amber-500/30',
        borderClass: 'border-yellow-400',
        icon: '🏆',
        intensity: 'legendary'
      };
    case 'eagle':
      return {
        bgClass: 'bg-gradient-to-r from-purple-500/30 to-pink-500/30',
        borderClass: 'border-purple-400',
        icon: '🦅',
        intensity: 'high'
      };
    case 'birdie':
      return {
        bgClass: 'bg-gradient-to-r from-green-500/20 to-emerald-500/20',
        borderClass: 'border-green-400',
        icon: '🐦',
        intensity: 'medium'
      };
    case 'ctp_submission':
      return {
        bgClass: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20',
        borderClass: 'border-blue-400',
        icon: '🎯',
        intensity: 'medium'
      };
    default:
      return {
        bgClass: 'bg-white/5',
        borderClass: 'border-white/20',
        icon: '📝',
        intensity: 'low'
      };
  }
}

// Funny "pencil whipping" messages
export const PENCIL_WHIP_MESSAGES = [
  { text: "You might want to count those strokes again 😂", subtext: "Pencil whipping is a crime!" },
  { text: "Are you sure you made that putt? 🤔", subtext: "The rules committee is watching..." },
  { text: "That's a nice score... a little TOO nice 🧐", subtext: "Double-check that math!" },
  { text: "Hold up! Did you count ALL the strokes? 😅", subtext: "We've got cameras everywhere" },
  { text: "Impressive! Almost suspiciously impressive... 🕵️", subtext: "Just saying..." }
];

// Get random pencil whip message
export function getRandomPencilWhipMessage() {
  return PENCIL_WHIP_MESSAGES[Math.floor(Math.random() * PENCIL_WHIP_MESSAGES.length)];
}
